import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import mammoth from "mammoth";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  FileUp,
  Loader2,
  Moon,
  Plus,
  Sparkles,
  Sun,
  X,
} from "lucide-react";

import { cn } from "../../../lib/utils";
import { queryClient } from "../../../lib/queryClient";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { trackHomeEvent } from "../../../lib/homeAnalytics";
import { listPwfs, createPwf, updatePwf, type Pwf } from "../../../api/pwf";
import { createSpace, listSpaces } from "../../../api/identitySpaces";
import { createTakeaway } from "../../../api/userTakeaways";
import {
  generateSeedPlan,
  generateSeedQuestions,
  type SeedPlan,
  type SeedQuestion,
  type SeedFollowup,
} from "../../../api/lifeOs";
import { buildLifeProjectConfig, buildSeedSuggestionCommits } from "./seederPersistence";
import { commitCapture } from "../../../api/capture";
import { normalizeLocale } from "../../../lib/startFlow";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Switch } from "../../ui/switch";

// ── The Seeder — Life OS onboarding interview ─────────────────────────────────
// A short conversational sequence: areas → what's alive → optional import
// (paste or .docx/.md/.txt) → adaptive follow-up questions → horizon → AI
// proposal → review (with a refine loop) → create. Nothing persists until the
// user confirms; creation uses only existing primitives. Additive mode seeds
// MORE into an already-arranged Life OS without duplicating what exists.
//
// Design identity: "the interview" — a warm paper page, not an app form. The
// wizard lives in its own paper world (fixed palette, deliberately the same in
// light and dark): serif questions in Newsreader, chapter cues instead of
// progress dots, ink buttons, and a review that reads as shelves of a life
// being arranged. The vow the visual language keeps making: nothing is created
// without your yes.

const ACTIVE_CAP = 3;

const AREA_KEYS = [
  "career",
  "health",
  "relationships",
  "finances",
  "learning",
  "creativity",
  "home",
  "inner",
] as const;

const DUE_OFFSET_MS: Record<string, number> = {
  today: 0,
  tomorrow: 24 * 3600 * 1000,
  this_week: 3 * 24 * 3600 * 1000,
  next_week: 7 * 24 * 3600 * 1000,
};

const compassOf = (pwfs: Pwf[]) =>
  pwfs.find((p) => !p.isTemplate && !p.pwfMeta?.fiParent && (p.config as any)?.cockpit) ?? null;

type Step =
  | "intro"
  | "areas"
  | "alive"
  | "import"
  | "followups"
  | "horizon"
  | "generating"
  | "review"
  | "creating"
  | "done";

// The six chapters of the interview, in reading order. The intro shows them as
// a table of contents; each screen carries its own "Chapter NN" kicker.
const CHAPTERS: { step: Step; nameKey: string }[] = [
  { step: "areas", nameKey: "seed_ch_areas" },
  { step: "alive", nameKey: "seed_ch_alive" },
  { step: "import", nameKey: "seed_ch_import" },
  { step: "followups", nameKey: "seed_ch_followups" },
  { step: "horizon", nameKey: "seed_ch_horizon" },
  { step: "review", nameKey: "seed_ch_review" },
];

const chapterNum = (step: Step) =>
  String(CHAPTERS.findIndex((c) => c.step === step) + 1).padStart(2, "0");

// ── Paper-world styling (shared fragments) ────────────────────────────────────
// The interview keeps its own fixed palette: paper #f8f4ec, ink #211d16,
// forest #2f5d50, ember #c2622e — a page, not a themed surface.

const KICKER = "text-[10px] font-semibold uppercase tracking-[0.18em] text-[#211d16]/40";
const INK_BTN =
  "inline-flex items-center gap-1.5 h-[42px] px-6 rounded-full bg-[#211d16] text-[#f8f4ec] text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-35 disabled:cursor-default cursor-pointer";
const GREEN_BTN =
  "inline-flex items-center gap-1.5 h-11 px-7 rounded-full bg-[#2f5d50] text-[#f8f4ec] text-[14.5px] font-medium transition-opacity hover:opacity-90 disabled:opacity-35 disabled:cursor-default cursor-pointer";
const QUIET_BTN =
  "text-[13px] text-[#211d16]/50 hover:text-[#211d16] transition-colors inline-flex items-center gap-1 cursor-pointer";
const INK_CHECKBOX =
  "rounded-[4px] border-[#211d16]/40 data-[state=checked]:bg-[#211d16] data-[state=checked]:border-[#211d16] data-[state=checked]:text-[#f8f4ec]";
const GREEN_SWITCH = "data-[state=checked]:bg-[#2f5d50]";
const NOTE_FIELD =
  "w-full rounded-md border border-[#211d16]/15 bg-[#fffdf8] px-4 py-3 text-sm leading-relaxed text-[#211d16] placeholder:text-[#211d16]/35 shadow-[0_2px_8px_rgba(33,29,22,0.06)] focus:outline-none focus:border-[#211d16]/35 resize-y";

// The review's identity cards and rhythm cards carry soft field tints (dynamic
// per index/ritual, so they stay inline styles rather than JIT classes).
const BECOMING_TINTS = [
  { background: "linear-gradient(160deg,#e7ece9,#f2f1ea)", borderColor: "rgba(47,93,80,0.25)" },
  { background: "linear-gradient(160deg,#f3e7d6,#f5f0e6)", borderColor: "rgba(194,98,46,0.25)" },
  { background: "linear-gradient(160deg,#e8e4f0,#f3f0ea)", borderColor: "rgba(74,78,124,0.25)" },
];

const RHYTHM_TINTS = {
  morning: { background: "linear-gradient(180deg,#f7e8d2,#f8f4ec)", borderColor: "rgba(194,98,46,0.25)", label: "#8a4a20" },
  evening: { background: "linear-gradient(180deg,#e3e1ef,#f5f2ec)", borderColor: "rgba(74,78,124,0.25)", label: "#4a4e7c" },
  weekly: { background: "linear-gradient(180deg,#dfe9e4,#f4f2ea)", borderColor: "rgba(47,93,80,0.25)", label: "#2f5d50" },
} as const;

// ── Draft persistence — closing the wizard must not lose the interview ───────

const DRAFT_KEY = "lifeos_seeder_draft";

interface SeederDraft {
  areas: string[];
  alive: string;
  imported: string;
  horizonDraft: string;
  followups: SeedFollowup[];
}

const loadDraft = (): SeederDraft | null => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as SeederDraft) : null;
  } catch {
    return null;
  }
};

// ── Invite card (shown on Today when the Life OS is empty) ────────────────────

const DISMISS_KEY = "lifeos_seeder_dismissed";

export const SeederInvite = () => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "1"
  );

  const { data: pwfs, isLoading } = useQuery({
    queryKey: ["cockpit-projects"],
    queryFn: () => listPwfs(retryFetch),
    staleTime: 30_000,
  });

  const alreadySeeded = useMemo(() => {
    if (!pwfs) return true;
    const hasProjects = pwfs.some(
      (p) => !p.isTemplate && !p.pwfMeta?.fiParent && (p.config as any)?.lifeProject
    );
    const horizon = ((compassOf(pwfs)?.config as any)?.cockpit?.futureStatement ?? "").trim();
    return hasProjects || horizon.length > 0;
  }, [pwfs]);

  // The card hides once seeded, but the open wizard must keep a stable tree
  // position — creation flips alreadySeeded mid-flow, and a branch switch
  // would remount the wizard and reset it before the done screen shows.
  const showCard = !isLoading && !alreadySeeded && !dismissed;

  return (
    <>
      {showCard && (
        <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-card to-card p-5">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-primary/10 p-2 mt-0.5">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-foreground">{t("seed_invite_title")}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t("seed_invite_text")}</p>
              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" className="rounded-full" onClick={() => setOpen(true)}>
                  {t("seed_invite_start")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-full text-muted-foreground"
                  onClick={() => {
                    localStorage.setItem(DISMISS_KEY, "1");
                    setDismissed(true);
                  }}
                >
                  {t("seed_invite_later")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {open && <LifeOsSeeder onClose={() => setOpen(false)} />}
    </>
  );
};

// ── "Seed more" entry — re-run the interview additively any time ─────────────

export const SeedMoreButton = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="rounded-full text-muted-foreground"
        onClick={() => setOpen(true)}
      >
        <Sparkles className="w-3.5 h-3.5 me-1.5" />
        {t("seed_more")}
      </Button>
      {open && <LifeOsSeeder additive onClose={() => setOpen(false)} />}
    </>
  );
};

// ── The wizard overlay ────────────────────────────────────────────────────────

const LifeOsSeeder: React.FC<{ onClose: () => void; additive?: boolean }> = ({
  onClose,
  additive,
}) => {
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();
  const lang = (i18n.language || "en").slice(0, 2);

  const draft = useRef(loadDraft()).current;
  const [step, setStep] = useState<Step>("intro");

  // Interview state (restored from the autosaved draft when present)
  const [areas, setAreas] = useState<string[]>(draft?.areas ?? []);
  const [customArea, setCustomArea] = useState("");
  const [alive, setAlive] = useState(draft?.alive ?? "");
  const [imported, setImported] = useState(draft?.imported ?? "");
  const [horizonDraft, setHorizonDraft] = useState(draft?.horizonDraft ?? "");
  const [genError, setGenError] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Adaptive follow-ups
  const [questions, setQuestions] = useState<SeedQuestion[] | null>(null);
  const [qLoading, setQLoading] = useState(false);
  const [answers, setAnswers] = useState<string[]>([]);

  // Proposal + review state
  const [plan, setPlan] = useState<SeedPlan | null>(null);
  const [spacesSel, setSpacesSel] = useState<boolean[]>([]);
  const [projectsSel, setProjectsSel] = useState<boolean[]>([]);
  const [placements, setPlacements] = useState<("active" | "resting")[]>([]);
  const [actionsSel, setActionsSel] = useState<boolean[]>([]);
  // Smart-capture suggestions: shown unticked (spec 2026-11-02 §6)
  const [mmSel, setMmSel] = useState<boolean[]>([]);
  const [qualitySel, setQualitySel] = useState<boolean[]>([]);
  const [horizonText, setHorizonText] = useState("");
  const [includeHorizon, setIncludeHorizon] = useState(true);
  const [rituals, setRituals] = useState<SeedPlan["rituals"] | null>(null);
  const [includeRituals, setIncludeRituals] = useState(true);
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineNote, setRefineNote] = useState("");
  const [genLine, setGenLine] = useState(0);

  // Creation progress
  const [createLog, setCreateLog] = useState<string[]>([]);
  const [failures, setFailures] = useState(0);
  const [created, setCreated] = useState({ spaces: 0, projects: 0, actions: 0 });

  // Autosave the interview while it's being answered
  useEffect(() => {
    if (step === "done") return;
    const payload: SeederDraft = {
      areas,
      alive,
      imported,
      horizonDraft,
      followups: (questions ?? []).map((q, i) => ({ q: q.question, a: answers[i] ?? "" })),
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
  }, [step, areas, alive, imported, horizonDraft, questions, answers]);

  const toggleArea = (label: string) =>
    setAreas((a) => (a.includes(label) ? a.filter((x) => x !== label) : [...a, label]));

  // ── File import: .docx via mammoth, .md/.txt as plain text ──────────────────
  const handleFile = async (file: File) => {
    setExtracting(true);
    try {
      let text = "";
      if (file.name.toLowerCase().endsWith(".docx")) {
        const buf = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer: buf });
        text = result.value;
      } else {
        text = await file.text();
      }
      text = text.trim().slice(0, 20000);
      if (text) {
        setImported((prev) => (prev ? `${prev}\n\n--- ${file.name} ---\n${text}` : `--- ${file.name} ---\n${text}`));
        toast.success(t("seed_file_added", { name: file.name }));
      } else {
        toast.error(t("seed_file_empty"));
      }
    } catch {
      toast.error(t("seed_file_failed"));
    } finally {
      setExtracting(false);
    }
  };

  // ── Adaptive follow-up round ────────────────────────────────────────────────
  const enterFollowups = async () => {
    setStep("followups");
    if (questions !== null) return; // already fetched (back navigation)
    setQLoading(true);
    try {
      const qs = await generateSeedQuestions(retryFetch, {
        areas,
        alive,
        imported: imported.trim() || undefined,
        language: lang,
      });
      setQuestions(qs);
      setAnswers(qs.map((_, i) => draft?.followups?.[i]?.a ?? ""));
    } catch {
      // Follow-ups are an enhancement, never a blocker — continue without them.
      setQuestions([]);
      setStep("horizon");
    } finally {
      setQLoading(false);
    }
  };

  // ── Generate / refine ───────────────────────────────────────────────────────
  const runGenerate = async (wantsHorizonHelp: boolean, refine?: { previousPlan: SeedPlan; note: string }) => {
    setStep("generating");
    setGenError(null);
    setGenLine(0);
    const iv = setInterval(() => setGenLine((i) => Math.min(i + 1, 2)), 6000);
    try {
      let existing: { spaces?: string[]; projects?: string[]; horizon?: string } | undefined;
      if (additive) {
        const [pwfs, spaces] = await Promise.all([listPwfs(retryFetch), listSpaces(retryFetch)]);
        existing = {
          spaces: (spaces ?? []).map((s: any) => s.name),
          projects: (pwfs ?? [])
            .filter((p) => !p.isTemplate && !p.pwfMeta?.fiParent && (p.config as any)?.lifeProject)
            .map((p) => p.title),
          horizon: ((compassOf(pwfs ?? [])?.config as any)?.cockpit?.futureStatement ?? "").trim() || undefined,
        };
      }
      const followups = (questions ?? [])
        .map((q, i) => ({ q: q.question, a: (answers[i] ?? "").trim() }))
        .filter((f) => f.a);
      const p = await generateSeedPlan(retryFetch, {
        areas,
        alive,
        imported: imported.trim() || undefined,
        followups: followups.length ? followups : undefined,
        horizonDraft: horizonDraft.trim() || undefined,
        wantsHorizonHelp,
        existing,
        refine,
        language: lang,
      });
      setPlan(p);
      setSpacesSel(p.spaces.map(() => true));
      setProjectsSel(p.projects.map(() => true));
      setPlacements(p.projects.map((pr) => pr.placement));
      setActionsSel(p.actionItems.map(() => true));
      setMmSel((p.mentalModels ?? []).map(() => false));
      setQualitySel((p.qualities ?? []).map(() => false));
      setHorizonText(p.horizon);
      setRituals(p.rituals);
      setRefineNote("");
      setRefineOpen(false);
      setStep("review");
    } catch (e: any) {
      setGenError(e.message ?? "Something went wrong");
      setStep(refine ? "review" : "horizon");
    } finally {
      clearInterval(iv);
    }
  };

  const journeyReasonFor = (title: string) =>
    plan?.journeySuggestions.find((j) => j.projectTitle === title)?.reason ?? null;

  // ── Create everything the user approved ─────────────────────────────────────
  const runCreate = async () => {
    if (!plan || !rituals) return;
    setStep("creating");
    const log: string[] = [];
    const push = (line: string) => {
      log.push(line);
      setCreateLog([...log]);
    };
    let fails = 0;
    const counts = { spaces: 0, projects: 0, actions: 0 };

    // 0. Existing spaces are valid link targets by name — in every mode, since
    // a first run can still reference a space that was created outside the
    // seeder, and additive runs always need this.
    const spaceIdByName: Record<string, string> = {};
    try {
      const existingSpaces = await listSpaces(retryFetch);
      for (const s of existingSpaces ?? []) spaceIdByName[(s as any).name] = (s as any)._id;
    } catch {
      /* links degrade gracefully */
    }

    // 1. Identity spaces (never duplicate an existing name)
    for (let i = 0; i < plan.spaces.length; i++) {
      if (!spacesSel[i]) continue;
      const s = plan.spaces[i];
      if (spaceIdByName[s.name]) continue;
      try {
        const createdSpace = await createSpace(retryFetch, {
          name: s.name,
          identityLabel: s.identityLabel ?? undefined,
          vision: s.vision ?? undefined,
          icon: s.icon,
          color: s.color ?? undefined,
          traits: s.traits && s.traits.length > 0 ? s.traits : undefined,
        });
        spaceIdByName[s.name] = (createdSpace as any)._id;
        counts.spaces++;
        push(t("seed_created_space", { name: s.name }));
      } catch {
        fails++;
      }
    }

    // 2. Compass node — horizon + ritual preferences (rituals engine reads these)
    if (includeHorizon || includeRituals) {
      try {
        const all = await listPwfs(retryFetch);
        const compass = compassOf(all);
        const cockpitCfg: Record<string, any> = {
          ...((compass?.config as any)?.cockpit ?? {}),
        };
        if (includeHorizon && horizonText.trim()) cockpitCfg.futureStatement = horizonText.trim();
        if (includeRituals) cockpitCfg.rituals = rituals;
        if (!cockpitCfg.seededAt) cockpitCfg.seededAt = new Date().toISOString();
        if (plan.seasonLine && !cockpitCfg.season) {
          cockpitCfg.season = {
            line: plan.seasonLine,
            setAt: new Date().toISOString(),
            source: "seeder",
          };
        }
        if (compass) {
          await updatePwf(retryFetch, compass._id, { config: { cockpit: cockpitCfg } });
        } else {
          await createPwf(retryFetch, {
            title: "My Compass",
            pwfType: "pwf",
            lifecycleState: "active",
            config: { cockpit: cockpitCfg },
          });
        }
        push(t("seed_created_horizon"));
      } catch {
        fails++;
      }
    }

    // 3. Life projects (respect the active cap in selection order)
    let activeCount = additive
      ? // existing active projects already occupy the cap
        await listPwfs(retryFetch)
          .then(
            (all) =>
              (all ?? []).filter(
                (p) =>
                  !p.isTemplate &&
                  !p.pwfMeta?.fiParent &&
                  (p.config as any)?.lifeProject?.status === "promote" &&
                  !["archived", "completed"].includes(p.lifecycleState)
              ).length
          )
          .catch(() => 0)
      : 0;
    const projectIdByTitle: Record<string, string> = {};
    for (let i = 0; i < plan.projects.length; i++) {
      if (!projectsSel[i]) continue;
      const pr = plan.projects[i];
      const wantsActive = placements[i] === "active" && activeCount < ACTIVE_CAP;
      if (wantsActive) activeCount++;
      try {
        const createdProject = await createPwf(retryFetch, {
          title: pr.title,
          pwfType: "pwf",
          lifecycleState: "active",
          config: {
            lifeProject: buildLifeProjectConfig(pr, spaceIdByName, plan.journeySuggestions ?? [], wantsActive),
          },
        });
        projectIdByTitle[pr.title] = (createdProject as any)._id;
        counts.projects++;
        push(t("seed_created_project", { title: pr.title }));
      } catch {
        fails++;
      }
    }

    // 4. Action items — the space link goes through spaceIds (category is a
    // UserCategory ObjectId in the BE, not a name).
    for (let i = 0; i < plan.actionItems.length; i++) {
      if (!actionsSel[i]) continue;
      const a = plan.actionItems[i];
      try {
        const spaceId = a.spaceName ? spaceIdByName[a.spaceName] : undefined;
        const projectId = a.projectTitle ? projectIdByTitle[a.projectTitle] : undefined;
        await createTakeaway(retryFetch, {
          type: "action",
          content: a.content,
          priority: a.priority,
          dueDate: a.due != null ? Date.now() + DUE_OFFSET_MS[a.due] : undefined,
          spaceIds: spaceId ? [spaceId] : undefined,
          projectId,
          source: { type: "user_created" },
        });
        counts.actions++;
      } catch {
        fails++;
      }
    }
    if (counts.actions > 0) push(t("seed_created_actions", { count: counts.actions }));

    // 5. Optional suggestions — mental models + qualities — through the smart-capture commit
    let suggestions = 0;
    for (const body of buildSeedSuggestionCommits(
      plan,
      { mentalModels: mmSel, qualities: qualitySel },
      spaceIdByName,
      normalizeLocale(lang)
    )) {
      try {
        await commitCapture(retryFetch, body);
        suggestions++;
      } catch {
        fails++;
      }
    }
    if (suggestions > 0) push(t("capture.seeder.created", { count: suggestions }));

    localStorage.removeItem(DRAFT_KEY);
    setFailures(fails);
    setCreated(counts);
    queryClient.invalidateQueries();
    trackHomeEvent("seeder_completed", {
      spaces: counts.spaces,
      projects: counts.projects,
      actions: counts.actions,
    });
    setStep("done");
  };

  const activeIdx = (plan?.projects ?? [])
    .map((_, i) => i)
    .filter((i) => placements[i] === "active");
  const restingIdx = (plan?.projects ?? [])
    .map((_, i) => i)
    .filter((i) => placements[i] !== "active");

  return (
    <div className="fixed inset-0 z-[60] bg-[#f8f4ec] text-[#211d16] overflow-y-auto">
      <div className="max-w-[700px] mx-auto px-6 sm:px-12 py-8 sm:py-11 min-h-full flex flex-col">
        {step === "intro" && (
          <div className="flex-1 flex gap-11">
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex items-start justify-between">
                <span className="font-interview text-xl text-[#2f5d50]" aria-hidden="true">✦</span>
                <CloseButton onClose={onClose} t={t} />
              </div>
              <h2 className="font-interview font-medium text-[28px] sm:text-[31px] leading-[1.2] tracking-[-0.01em] mt-5">
                {additive ? t("seed_more_intro_title") : t("seed_intro_title")}
              </h2>
              <p className="text-sm leading-[1.65] text-[#211d16]/60 mt-3.5">
                {additive ? t("seed_more_intro_text") : t("seed_intro_text")}
              </p>
              <p className="font-interview italic text-[12.5px] leading-relaxed text-[#2f5d50] mt-3.5">
                {t("seed_vow")}
              </p>
              {draft && (draft.alive || draft.areas.length > 0) && (
                <p className="text-xs text-[#211d16]/55 bg-[#211d16]/[0.04] rounded-lg px-3 py-2 self-start mt-5">
                  {t("seed_draft_restored")}
                </p>
              )}
              <div className="flex-1 min-h-8" />
              <div className="flex items-center gap-5">
                <button className={INK_BTN} onClick={() => setStep("areas")}>
                  {t("seed_begin")}
                </button>
                <button className={QUIET_BTN} onClick={onClose}>
                  {t("seed_not_now")}
                </button>
              </div>
            </div>
            {/* The chapter map — a table of contents replaces progress dots */}
            <div className="hidden sm:flex w-[170px] flex-shrink-0 flex-col gap-[15px] border-s border-[#211d16]/10 ps-6 pt-2">
              <p className={KICKER}>{t("seed_chapters")}</p>
              {CHAPTERS.map((c, i) => (
                <p
                  key={c.step}
                  className={cn(
                    "text-[13px] leading-tight",
                    i === 0 ? "font-semibold text-[#211d16]" : "text-[#211d16]/45"
                  )}
                >
                  {String(i + 1).padStart(2, "0")}&nbsp;&nbsp;{t(c.nameKey)}
                </p>
              ))}
            </div>
          </div>
        )}

        {step === "areas" && (
          <ChapterShell
            step={step}
            title={t("seed_areas_title")}
            subtitle={t("seed_areas_text")}
            primary={t("seed_continue")}
            primaryDisabled={areas.length === 0}
            onPrimary={() => setStep("alive")}
            onClose={onClose}
            t={t}
          >
            <div className="flex flex-wrap gap-2.5">
              {AREA_KEYS.map((k) => {
                const label = t(`seed_area_${k}`);
                return <AreaChip key={k} label={label} on={areas.includes(label)} onToggle={() => toggleArea(label)} />;
              })}
              {areas
                .filter((a) => !AREA_KEYS.some((k) => t(`seed_area_${k}`) === a))
                .map((a) => (
                  <AreaChip key={a} label={a} on onToggle={() => toggleArea(a)} />
                ))}
            </div>
            <div className="flex items-end gap-2 mt-6">
              <input
                value={customArea}
                onChange={(e) => setCustomArea(e.target.value)}
                placeholder={t("seed_area_custom")}
                className="max-w-[260px] bg-transparent border-b border-[#211d16]/25 focus:border-[#211d16]/60 focus:outline-none pb-1.5 font-interview italic text-[15.5px] placeholder:text-[#211d16]/40"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && customArea.trim()) {
                    toggleArea(customArea.trim());
                    setCustomArea("");
                  }
                }}
              />
              {customArea.trim() && (
                <button
                  className="p-1.5 rounded-full border border-[#211d16]/25 text-[#211d16]/70 hover:text-[#211d16] hover:border-[#211d16]/50 transition-colors cursor-pointer"
                  aria-label={t("seed_continue")}
                  onClick={() => {
                    toggleArea(customArea.trim());
                    setCustomArea("");
                  }}
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </ChapterShell>
        )}

        {step === "alive" && (
          <ChapterShell
            step={step}
            title={t("seed_alive_title")}
            subtitle={t("seed_alive_text", { areas: areas.join(" · ") })}
            primary={t("seed_continue")}
            primaryDisabled={alive.trim().length < 10}
            onPrimary={() => setStep("import")}
            onBack={() => setStep("areas")}
            onClose={onClose}
            t={t}
          >
            <textarea
              value={alive}
              onChange={(e) => setAlive(e.target.value)}
              placeholder={t("seed_alive_placeholder")}
              rows={10}
              autoFocus
              className={NOTE_FIELD}
            />
          </ChapterShell>
        )}

        {step === "import" && (
          <ChapterShell
            step={step}
            title={t("seed_import_title")}
            subtitle={t("seed_import_text")}
            primary={imported.trim() ? t("seed_continue") : t("seed_skip")}
            onPrimary={enterFollowups}
            onBack={() => setStep("alive")}
            onClose={onClose}
            t={t}
          >
            {/* A paper note pasted onto the page — slightly tilted, mono hand */}
            <div className="rotate-[-0.4deg]">
              <textarea
                value={imported}
                onChange={(e) => setImported(e.target.value)}
                placeholder={t("seed_import_placeholder")}
                rows={9}
                className={cn(NOTE_FIELD, "rounded-[4px] font-mono text-xs leading-[1.75] shadow-[0_2px_8px_rgba(33,29,22,0.08)]")}
              />
            </div>
            <div className="flex items-center gap-3 mt-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx,.md,.txt"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                  e.target.value = "";
                }}
              />
              <button
                className="inline-flex items-center gap-1.5 h-[34px] px-4 rounded-full border border-dashed border-[#211d16]/35 text-[12.5px] text-[#211d16] hover:border-[#211d16]/60 transition-colors cursor-pointer disabled:opacity-50"
                disabled={extracting}
                onClick={() => fileInputRef.current?.click()}
              >
                {extracting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <FileUp className="w-3.5 h-3.5" />
                )}
                {t("seed_upload_file")} ({t("seed_upload_hint")})
              </button>
              <span className="text-xs text-[#211d16]/45">{t("seed_keep_typing")}</span>
            </div>
          </ChapterShell>
        )}

        {step === "followups" && (
          <ChapterShell
            step={step}
            title={t("seed_followups_title")}
            subtitle={t("seed_followups_text")}
            primary={t("seed_continue")}
            primaryDisabled={qLoading}
            onPrimary={() => setStep("horizon")}
            onBack={() => setStep("import")}
            onClose={onClose}
            t={t}
          >
            {qLoading ? (
              <div className="flex items-center gap-3 text-sm text-[#211d16]/60 py-8">
                <Loader2 className="w-4 h-4 animate-spin text-[#2f5d50]" />
                {t("seed_followups_loading")}
              </div>
            ) : (
              <div className="space-y-6">
                {(questions ?? []).map((q, i) => (
                  <div key={i}>
                    <p className="font-interview text-[17px] leading-snug">{q.question}</p>
                    <p className="text-[11px] text-[#211d16]/45 mt-0.5 mb-2">{q.why}</p>
                    <textarea
                      value={answers[i] ?? ""}
                      onChange={(e) =>
                        setAnswers((a) => a.map((x, j) => (j === i ? e.target.value : x)))
                      }
                      rows={2}
                      className={NOTE_FIELD}
                      placeholder={t("seed_followups_optional")}
                    />
                  </div>
                ))}
              </div>
            )}
          </ChapterShell>
        )}

        {step === "horizon" && (
          <ChapterShell
            step={step}
            title={t("seed_horizon_title")}
            subtitle={t("seed_horizon_text")}
            primary={horizonDraft.trim() ? t("seed_generate") : t("seed_horizon_draft_for_me")}
            onPrimary={() => runGenerate(!horizonDraft.trim())}
            onBack={() => setStep("followups")}
            onClose={onClose}
            t={t}
          >
            <textarea
              value={horizonDraft}
              onChange={(e) => setHorizonDraft(e.target.value)}
              placeholder={t("cockpit_statement_placeholder")}
              rows={6}
              className={cn(NOTE_FIELD, "font-interview italic text-[15px] leading-[1.7]")}
            />
            {genError && (
              <p className="text-sm text-[#b3402a] mt-3">
                {t("seed_error")} <span className="opacity-70">({genError})</span>
              </p>
            )}
          </ChapterShell>
        )}

        {step === "generating" && (
          <div className="flex-1 flex flex-col">
            <div className="flex justify-end">
              <CloseButton onClose={onClose} t={t} />
            </div>
            <div className="flex-1 flex flex-col items-center justify-center text-center pb-16">
              {/* The proposal condensing — a small green world, not a spinner */}
              <div
                className="w-[74px] h-[74px] rounded-full mb-8 motion-safe:animate-pulse"
                style={{
                  background: "radial-gradient(circle at 38% 32%, #e9f2ee, #9dbfae 55%, #2f5d50)",
                  boxShadow: "0 0 44px 10px rgba(47,93,80,0.28)",
                }}
                aria-hidden="true"
              />
              <p className="font-interview text-[21px]">{t("seed_generating")}</p>
              <div className="mt-7 flex flex-col gap-2.5 text-start">
                {[0, 1, 2].map((i) => (
                  <GenPhaseLine key={i} state={i < genLine ? "done" : i === genLine ? "current" : "pending"}>
                    {t(`seed_generating_${i}` as any)}
                  </GenPhaseLine>
                ))}
              </div>
              <p className="text-xs text-[#211d16]/45 mt-7">{t("seed_generating_hint")}</p>
            </div>
          </div>
        )}

        {step === "review" && plan && rituals && (
          <div className="pb-12">
            <div className="flex items-baseline justify-between">
              <p className={KICKER}>
                {t("seed_chapter_kicker", { num: chapterNum("review"), name: t("seed_ch_review") })}
              </p>
              <CloseButton onClose={onClose} t={t} />
            </div>
            <h2 className="font-interview font-medium text-[26px] sm:text-[28px] leading-[1.25] mt-5">
              {t("seed_review_title")}
            </h2>
            <p className="font-interview italic text-[15.5px] leading-[1.6] text-[#211d16]/65 mt-2.5">
              {plan.welcomeNote}
            </p>
            <p className="text-xs text-[#211d16]/45 mt-2">{t("seed_review_hint")}</p>

            {/* Who you're becoming — the identity spaces */}
            {plan.spaces.length > 0 && (
              <section className="mt-8">
                <p className={cn(KICKER, "mb-2.5")}>{t("seed_shelf_becoming")}</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  {plan.spaces.map((s, i) => {
                    const on = spacesSel[i];
                    return (
                      <button
                        key={i}
                        onClick={() =>
                          setSpacesSel((sel) => sel.map((x, j) => (j === i ? !x : x)))
                        }
                        className={cn(
                          "relative rounded-[14px] border p-3.5 text-start transition-all cursor-pointer",
                          !on && "opacity-45 border-dashed"
                        )}
                        style={on ? BECOMING_TINTS[i % 3] : { borderColor: "rgba(33,29,22,0.3)" }}
                        aria-pressed={on}
                      >
                        <span
                          className={cn(
                            "absolute top-2.5 end-2.5 h-[18px] w-[18px] rounded-full flex items-center justify-center",
                            on ? "bg-[#2f5d50] text-[#f8f4ec]" : "border border-[#211d16]/25"
                          )}
                        >
                          {on && <Check className="w-3 h-3" />}
                        </span>
                        <p className="font-interview text-[17px] leading-snug pe-6">{s.name}</p>
                        {(s.identityLabel || s.vision) && (
                          <p className="text-[11.5px] leading-[1.5] text-[#211d16]/60 mt-1">
                            {[s.identityLabel, s.vision].filter(Boolean).join(" — ")}
                          </p>
                        )}
                        {s.traits && s.traits.length > 0 && (
                          <span className="flex flex-wrap gap-1 mt-2">
                            {s.traits.map((tr) => (
                              <span
                                key={tr}
                                className="rounded-full border border-[#211d16]/15 px-2 py-0.5 text-[10.5px] text-[#211d16]/55"
                              >
                                {tr}
                              </span>
                            ))}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* In motion — the projects that get the coming weeks */}
            {plan.projects.length > 0 && (
              <section className="mt-8">
                <p className={cn(KICKER, "mb-2.5")}>{t("seed_shelf_active", { cap: ACTIVE_CAP })}</p>
                <div className="space-y-2">
                  {activeIdx.map((i) => {
                    const pr = plan.projects[i];
                    const reason = journeyReasonFor(pr.title);
                    return (
                      <div
                        key={i}
                        className={cn(
                          "flex items-center gap-3 bg-[#fffdf8] border border-[#c2622e]/30 rounded-xl px-3.5 py-[11px]",
                          !projectsSel[i] && "opacity-45"
                        )}
                      >
                        <Checkbox
                          checked={projectsSel[i]}
                          onCheckedChange={(v) =>
                            setProjectsSel((sel) => sel.map((x, j) => (j === i ? Boolean(v) : x)))
                          }
                          className={INK_CHECKBOX}
                        />
                        <Sun className="w-3.5 h-3.5 text-[#c2622e] flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold leading-snug">{pr.title}</p>
                          <p className="text-[11.5px] text-[#211d16]/55 mt-px">
                            {[pr.spaceName, pr.whyNow].filter(Boolean).join(" · ")}
                            {reason && (
                              <span className="text-[#2f5d50]">
                                {pr.spaceName || pr.whyNow ? " · " : ""}✦ {t("seed_journey_badge")}
                              </span>
                            )}
                          </p>
                        </div>
                        <button
                          className="text-[11px] text-[#211d16]/45 hover:text-[#211d16] transition-colors whitespace-nowrap cursor-pointer"
                          onClick={() =>
                            setPlacements((pl) => pl.map((x, j) => (j === i ? "resting" : x)))
                          }
                        >
                          {t("seed_rest_it")} ↓
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Resting — safe, not forgotten */}
                {restingIdx.length > 0 && (
                  <>
                    <p className={cn(KICKER, "mt-5 mb-2.5")}>{t("seed_shelf_resting")}</p>
                    <div className="flex flex-wrap gap-2">
                      {restingIdx.map((i) => {
                        const pr = plan.projects[i];
                        return (
                          <span
                            key={i}
                            className={cn(
                              "inline-flex items-center gap-2 border border-[#211d16]/15 bg-[#211d16]/[0.03] rounded-full py-1.5 ps-2.5 pe-3 text-[12.5px] text-[#211d16]/70",
                              !projectsSel[i] && "opacity-45"
                            )}
                          >
                            <Checkbox
                              checked={projectsSel[i]}
                              onCheckedChange={(v) =>
                                setProjectsSel((sel) => sel.map((x, j) => (j === i ? Boolean(v) : x)))
                              }
                              className={cn(INK_CHECKBOX, "h-3.5 w-3.5")}
                            />
                            <Moon className="w-3 h-3 flex-shrink-0" />
                            {pr.title}
                            <button
                              className="text-[11px] text-[#211d16]/45 hover:text-[#211d16] transition-colors cursor-pointer"
                              onClick={() =>
                                setPlacements((pl) => pl.map((x, j) => (j === i ? "active" : x)))
                              }
                            >
                              {t("seed_wake_it")} ↑
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </>
                )}
              </section>
            )}

            {/* First steps */}
            {plan.actionItems.length > 0 && (
              <section className="mt-8">
                <p className={cn(KICKER, "mb-2.5")}>{t("seed_review_actions")}</p>
                <div className="space-y-1.5">
                  {plan.actionItems.map((a, i) => (
                    <label
                      key={i}
                      className={cn(
                        "flex items-center gap-3 bg-[#fffdf8] border border-[#211d16]/10 rounded-xl px-3 py-2 cursor-pointer",
                        !actionsSel[i] && "opacity-45"
                      )}
                    >
                      <Checkbox
                        checked={actionsSel[i]}
                        onCheckedChange={(v) =>
                          setActionsSel((sel) => sel.map((x, j) => (j === i ? Boolean(v) : x)))
                        }
                        className={INK_CHECKBOX}
                      />
                      <span
                        className={cn(
                          "w-1.5 h-1.5 rounded-full flex-shrink-0",
                          a.priority === "high"
                            ? "bg-[#c2622e]"
                            : a.priority === "medium"
                              ? "bg-[#c99a3d]"
                              : "bg-[#211d16]/20"
                        )}
                      />
                      <span className="text-sm flex-1 min-w-0">{a.content}</span>
                      {a.due && (
                        <span className="text-[11px] text-[#211d16]/45 flex-shrink-0">
                          {t(`seed_due_${a.due}`)}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </section>
            )}

            {/* Optional: ways of seeing + qualities to grow — unticked by default */}
            {[
              {
                key: "mentalModels",
                title: t("capture.seeder.mentalModels"),
                rows: (plan.mentalModels ?? []).map((m) => ({ text: m.statement, tag: t(`capture.mmType.${m.mmType}`) })),
                sel: mmSel,
                setSel: setMmSel,
              },
              {
                key: "qualities",
                title: t("capture.seeder.qualities"),
                rows: (plan.qualities ?? []).map((q) => ({ text: q.name, tag: q.spaceName ?? "" })),
                sel: qualitySel,
                setSel: setQualitySel,
              },
            ]
              .filter((g) => g.rows.length > 0)
              .map((g) => (
                <section key={g.key} className="mt-8">
                  <p className={cn(KICKER, "mb-1")}>{g.title}</p>
                  <p className="text-xs text-[#211d16]/45 mb-2.5">{t("capture.seeder.hint")}</p>
                  <div className="space-y-1.5">
                    {g.rows.map((row, i) => (
                      <label
                        key={i}
                        className={cn(
                          "flex items-center gap-3 bg-[#fffdf8] border border-[#211d16]/10 rounded-xl px-3 py-2 cursor-pointer",
                          !g.sel[i] && "opacity-60"
                        )}
                      >
                        <Checkbox
                          checked={g.sel[i] ?? false}
                          onCheckedChange={(v) => g.setSel((sel) => sel.map((x, j) => (j === i ? Boolean(v) : x)))}
                          className={INK_CHECKBOX}
                        />
                        <span dir="auto" className="text-sm flex-1 min-w-0 text-start">{row.text}</span>
                        {row.tag && <span className="text-[11px] text-[#211d16]/45 flex-shrink-0">{row.tag}</span>}
                      </label>
                    ))}
                  </div>
                </section>
              ))}

            {/* The horizon — a year from now, in the user's own hand */}
            <section className="mt-8">
              <div className="flex items-center justify-between mb-2.5">
                <p className={KICKER}>{t("seed_shelf_horizon")}</p>
                <Switch
                  checked={includeHorizon}
                  onCheckedChange={setIncludeHorizon}
                  className={GREEN_SWITCH}
                />
              </div>
              {includeHorizon && (
                <div className="border-s-2 border-[#2f5d50] ps-[18px] py-0.5">
                  <textarea
                    value={horizonText}
                    onChange={(e) => setHorizonText(e.target.value)}
                    rows={4}
                    className="w-full bg-transparent focus:outline-none font-interview italic text-[16.5px] leading-[1.7] resize-y"
                  />
                </div>
              )}
            </section>

            {/* The rhythm — the companion's standing appointments */}
            <section className="mt-8">
              <div className="flex items-center justify-between mb-1">
                <p className={KICKER}>{t("seed_shelf_rhythm")}</p>
                <Switch
                  checked={includeRituals}
                  onCheckedChange={setIncludeRituals}
                  className={GREEN_SWITCH}
                />
              </div>
              {includeRituals && (
                <>
                  <p className="text-[11.5px] italic text-[#211d16]/50 mb-2.5">{rituals.reason}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {(["morning", "evening", "weekly"] as const).map((k) => {
                      const r = rituals[k];
                      const tint = RHYTHM_TINTS[k];
                      return (
                        <div
                          key={k}
                          className={cn("rounded-[14px] border p-3.5", !r.enabled && "opacity-45")}
                          style={{ background: tint.background, borderColor: tint.borderColor }}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[12.5px] font-semibold" style={{ color: tint.label }}>
                              {t(`seed_ritual_${k}`)}
                            </p>
                            <Switch
                              checked={r.enabled}
                              onCheckedChange={(v) =>
                                setRituals({ ...rituals, [k]: { ...r, enabled: v } })
                              }
                              className={cn(GREEN_SWITCH, "scale-[0.78] origin-center -me-1")}
                            />
                          </div>
                          {r.enabled && (
                            <div className="flex items-center gap-1.5 mt-1.5">
                              {k === "weekly" && (
                                <select
                                  value={rituals.weekly.day}
                                  onChange={(e) =>
                                    setRituals({
                                      ...rituals,
                                      weekly: { ...rituals.weekly, day: e.target.value },
                                    })
                                  }
                                  className="bg-transparent font-interview text-[15px] text-[#211d16] focus:outline-none cursor-pointer max-w-[96px]"
                                >
                                  {DAYS.map((d) => (
                                    <option key={d} value={d}>
                                      {t(`seed_day_${d}`)}
                                    </option>
                                  ))}
                                </select>
                              )}
                              <input
                                type="time"
                                value={r.time}
                                onChange={(e) =>
                                  setRituals({ ...rituals, [k]: { ...r, time: e.target.value } })
                                }
                                className="bg-transparent font-interview text-base text-[#211d16] focus:outline-none cursor-pointer w-[86px]"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-[#211d16]/45 mt-2">{t("seed_rituals_live")}</p>
                </>
              )}
            </section>

            {/* Footer — ask for changes, or make it real */}
            <div className="mt-10">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-5">
                  <button className={QUIET_BTN} onClick={() => setStep("horizon")}>
                    <ArrowLeft className="w-3.5 h-3.5 rtl:-scale-x-100" />
                    {t("seed_back")}
                  </button>
                  <button
                    className="text-[13px] text-[#211d16]/55 hover:text-[#211d16] border-b border-[#211d16]/30 pb-px transition-colors cursor-pointer"
                    onClick={() => setRefineOpen((o) => !o)}
                  >
                    {t("seed_ask_changes")}
                  </button>
                </div>
                <button className={GREEN_BTN} onClick={runCreate}>
                  {t("seed_create_all")}
                </button>
              </div>
              {refineOpen && (
                <div className="flex items-start gap-2 mt-4">
                  <textarea
                    value={refineNote}
                    onChange={(e) => setRefineNote(e.target.value)}
                    placeholder={t("seed_refine_placeholder")}
                    rows={2}
                    autoFocus
                    className={cn(NOTE_FIELD, "flex-1")}
                  />
                  <button
                    className={cn(INK_BTN, "h-9 px-4 text-[13px] mt-1")}
                    disabled={!refineNote.trim()}
                    onClick={() => runGenerate(false, { previousPlan: plan, note: refineNote.trim() })}
                  >
                    {t("seed_refine_go")}
                  </button>
                </div>
              )}
              {genError && (
                <p className="text-xs text-[#b3402a] mt-3">{t("seed_error")}</p>
              )}
            </div>
          </div>
        )}

        {step === "creating" && (
          <div className="flex-1 flex flex-col items-center justify-center py-24">
            <div
              className="w-[54px] h-[54px] rounded-full mb-8 motion-safe:animate-pulse"
              style={{
                background: "radial-gradient(circle at 38% 32%, #e9f2ee, #9dbfae 55%, #2f5d50)",
                boxShadow: "0 0 36px 8px rgba(47,93,80,0.25)",
              }}
              aria-hidden="true"
            />
            <div className="space-y-2 text-center">
              {createLog.slice(-6).map((line, i) => (
                <p key={i} className="text-sm text-[#211d16]/70 flex items-center gap-2 justify-center">
                  <Check className="w-3.5 h-3.5 text-[#2f5d50]" />
                  {line}
                </p>
              ))}
            </div>
          </div>
        )}

        {step === "done" && (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-24">
            <div className="rounded-full bg-[#2f5d50]/10 p-4 mb-5 shadow-[0_0_32px_8px_rgba(47,93,80,0.18)]">
              <Check className="w-8 h-8 text-[#2f5d50]" />
            </div>
            <h2 className="font-interview font-medium text-[26px] leading-tight">{t("seed_done_title")}</h2>
            <p className="text-sm leading-[1.65] text-[#211d16]/60 mt-3 max-w-sm">
              {t("seed_done_text", {
                spaces: created.spaces,
                projects: created.projects,
                actions: created.actions,
              })}
            </p>
            {failures > 0 && (
              <p className="text-xs text-[#b3402a] mt-2">{t("seed_done_failures", { count: failures })}</p>
            )}
            <button
              className={cn(GREEN_BTN, "mt-7")}
              onClick={() => {
                toast.success(t("seed_done_toast"));
                onClose();
              }}
            >
              {t("seed_done_enter")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Small pieces ──────────────────────────────────────────────────────────────

const CloseButton: React.FC<{ onClose: () => void; t: (k: string) => string }> = ({
  onClose,
  t,
}) => (
  <button
    onClick={onClose}
    className="p-1.5 -m-1.5 text-[#211d16]/40 hover:text-[#211d16] transition-colors cursor-pointer"
    aria-label={t("cancel")}
  >
    <X className="w-4 h-4" />
  </button>
);

// One chapter of the interview: kicker, a serif question that fills the page,
// the answer surface, and a quiet back / ink continue footer.
const ChapterShell: React.FC<{
  step: Step;
  title: string;
  subtitle: string;
  primary: string;
  primaryDisabled?: boolean;
  onPrimary: () => void;
  onBack?: () => void;
  onClose: () => void;
  t: (k: string, o?: any) => string;
  children?: React.ReactNode;
}> = ({ step, title, subtitle, primary, primaryDisabled, onPrimary, onBack, onClose, t, children }) => (
  <div className="flex-1 flex flex-col">
    <div className="flex items-baseline justify-between">
      <p className={KICKER}>
        {t("seed_chapter_kicker", {
          num: chapterNum(step),
          name: t(CHAPTERS.find((c) => c.step === step)!.nameKey),
        })}
      </p>
      <CloseButton onClose={onClose} t={t} />
    </div>
    <h2 className="font-interview font-medium text-[25px] sm:text-[28px] leading-[1.25] mt-6">{title}</h2>
    <p className="text-[13.5px] leading-relaxed text-[#211d16]/60 mt-2.5 mb-7 whitespace-pre-line">
      {subtitle}
    </p>
    <div className="flex-1">{children}</div>
    <div className="flex items-center justify-between pt-8">
      {onBack ? (
        <button className={QUIET_BTN} onClick={onBack}>
          <ArrowLeft className="w-3.5 h-3.5 rtl:-scale-x-100" />
          {t("seed_back")}
        </button>
      ) : (
        <span />
      )}
      <button className={INK_BTN} disabled={primaryDisabled} onClick={onPrimary}>
        {primary}
        <ArrowRight className="w-4 h-4 rtl:-scale-x-100" />
      </button>
    </div>
  </div>
);

const AreaChip: React.FC<{ label: string; on: boolean; onToggle: () => void }> = ({
  label,
  on,
  onToggle,
}) => (
  <button
    onClick={onToggle}
    aria-pressed={on}
    className={cn(
      "rounded-full px-[18px] py-[9px] font-interview text-[15.5px] transition-colors cursor-pointer",
      on
        ? "bg-[#211d16] text-[#f8f4ec]"
        : "border border-[#211d16]/25 text-[#211d16] hover:border-[#211d16]/55"
    )}
  >
    {label}
  </button>
);

// The generating screen shows the work as visible phases, not a spinner.
const GenPhaseLine: React.FC<{ state: "done" | "current" | "pending"; children: React.ReactNode }> = ({
  state,
  children,
}) => (
  <p
    className={cn(
      "text-[13px] flex items-center gap-2",
      state === "done" ? "text-[#2f5d50]" : state === "current" ? "text-[#211d16]" : "text-[#211d16]/40"
    )}
  >
    {state === "done" ? (
      <Check className="w-[13px] h-[13px] flex-shrink-0" strokeWidth={2.5} />
    ) : state === "current" ? (
      <span className="w-[13px] h-[13px] rounded-full border-2 border-[#2f5d50] border-t-transparent animate-spin flex-shrink-0" />
    ) : (
      <span className="w-[13px] h-[13px] rounded-full border-[1.5px] border-[#211d16]/25 flex-shrink-0" />
    )}
    {children}
  </p>
);

const DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export default LifeOsSeeder;
